import { useMemo, useRef, useState } from 'react';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PlayCircle, Settings, Loader2 } from 'lucide-react';
import { TestResult, EvalResult, SourceEvalResult } from '@/types/evals-types';
import { safeFormatDate } from '@/utils/format-date';
import { isUrl } from '@/utils/url-utils';
import { truncateText } from '@/utils/text-utils';

interface SourceContentRendererProps {
  content: string;
  sourceId: string;
}

export function SourceContentRenderer({
  content,
  sourceId,
}: SourceContentRendererProps) {
  const contentIsUrl = isUrl(content);

  if (contentIsUrl) {
    return (
      <a
        href={content}
        target='_blank'
        rel='noopener noreferrer'
        className='text-blue-500 hover:text-blue-700 underline'
      >
        {content}
      </a>
    );
  }

  const { truncated, isTruncated } = truncateText(content);

  if (isTruncated) {
    return (
      <Dialog>
        <DialogTrigger asChild>
          <div
            className='cursor-pointer hover:bg-accent rounded px-1 py-0.5 transition-colors'
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {truncated}
          </div>
        </DialogTrigger>
        <DialogContent className='max-w-2xl max-h-[80vh]'>
          <DialogHeader>
            <DialogTitle>Full Content</DialogTitle>
            <DialogDescription>Source ID: {sourceId}</DialogDescription>
          </DialogHeader>
          <div className='max-h-[60vh] overflow-y-auto p-4 border rounded-md bg-muted/10'>
            <p className='whitespace-pre-wrap break-words text-sm'>{content}</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <div
      style={{
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}
    >
      {content}
    </div>
  );
}

interface VerdictBadgeProps {
  verdict?: string;
}

export function VerdictBadge({ verdict }: VerdictBadgeProps) {
  if (!verdict) return null;

  return (
    <Badge
      className={
        verdict === 'PASS'
          ? 'bg-green-500'
          : verdict === 'FAIL'
            ? 'bg-red-500'
            : 'bg-muted text-muted-foreground'
      }
    >
      {verdict === 'PASS' ? 'PASS' : verdict === 'FAIL' ? 'FAIL' : verdict}
    </Badge>
  );
}

interface EvaluationResultCellProps {
  result: SourceEvalResult;
  evalId: string;
}

export function EvaluationResultCell({
  result,
  evalId,
}: EvaluationResultCellProps) {
  const hasExplanation = !!result.response.explanation;
  const hasScore = typeof result.response.score !== 'undefined';

  const resultContent = (
    <span className='inline-flex items-center'>
      <VerdictBadge verdict={result.response.verdict} />
      {hasScore && <span className='font-medium'>{result.response.score}</span>}
    </span>
  );

  if (hasExplanation) {
    return (
      <Dialog>
        <DialogTrigger asChild>
          <TableCell
            className='min-w-[150px] max-w-[100px] break-words whitespace-normal cursor-pointer hover:bg-accent'
            tabIndex={0}
          >
            {resultContent}
          </TableCell>
        </DialogTrigger>
        <DialogContent className='max-w-xs whitespace-pre-line'>
          <DialogHeader>
            <DialogTitle>Explanation</DialogTitle>
          </DialogHeader>
          <DialogDescription>
            {Array.isArray(result.response.explanation)
              ? result.response.explanation.join(' ')
              : String(result.response.explanation)}
          </DialogDescription>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <TableCell
      key={evalId}
      className='min-w-[150px] max-w-[100px] break-words whitespace-normal'
    >
      {resultContent}
    </TableCell>
  );
}

interface TestConfigDialogProps {
  testResult: TestResult;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TestConfigDialog({
  testResult,
  open,
  onOpenChange,
}: TestConfigDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant='outline' size='sm' className='flex items-center gap-1'>
          <Settings className='h-4 w-4' />
          View Config
        </Button>
      </DialogTrigger>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>Test Configuration</DialogTitle>
          <DialogDescription>
            Details of the test configuration.
          </DialogDescription>
        </DialogHeader>
        {testResult ? (
          <div className='space-y-4 py-4'>
            <div className='space-y-2'>
              <h3 className='text-sm font-medium'>Test Name</h3>
              <p className='text-sm'>{testResult.name}</p>
            </div>
            <div className='space-y-2'>
              <h3 className='text-sm font-medium'>Evaluations</h3>
              <ul className='list-disc list-inside text-sm'>
                {testResult.eval_results?.map((evalResult) => (
                  <li key={evalResult.eval_id}>
                    {evalResult.eval_name || evalResult.eval_id}
                  </li>
                ))}
              </ul>
            </div>
            <div className='space-y-2'>
              <h3 className='text-sm font-medium'>Date</h3>
              <p className='text-sm'>
                {safeFormatDate(
                  new Date(testResult.created_at),
                  'MMMM d, yyyy',
                )}
              </p>
            </div>
          </div>
        ) : (
          <div className='text-muted-foreground'>No config available.</div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface TestActionsHeaderProps {
  testResult: TestResult;
  onRerun?: (testId: string) => void;
  pollingTestId?: string | null;
}

export function TestActionsHeader({
  testResult,
  onRerun,
  pollingTestId,
}: TestActionsHeaderProps) {
  const [showConfig, setShowConfig] = useState(false);
  const [rerunLoading, setRerunLoading] = useState(false);
  const testId = (testResult?.testid as string | undefined) || null;
  const isPolling = pollingTestId === testId;

  return (
    <div className='flex justify-end items-start'>
      <div className='flex items-center space-x-2'>
        <Button
          onClick={async () => {
            if (!onRerun || !testResult) return;
            setRerunLoading(true);
            try {
              await onRerun(testResult.testid as string);
            } finally {
              setRerunLoading(false);
            }
          }}
          variant='outline'
          size='sm'
          className='flex items-center gap-1'
          disabled={rerunLoading || isPolling}
        >
          {rerunLoading || isPolling ? (
            <>
              <Loader2 className='h-4 w-4 animate-spin' />
              Running...
            </>
          ) : (
            <>
              <PlayCircle className='h-4 w-4' />
              Run Again
            </>
          )}
        </Button>
        <TestConfigDialog
          testResult={testResult}
          open={showConfig}
          onOpenChange={setShowConfig}
        />
      </div>
    </div>
  );
}

interface TestResultTableProps {
  testResult: TestResult;
  getAllSources: (evalResults: EvalResult[]) => string[];
  getResultForSource: (
    evalResult: EvalResult,
    sourceId: string,
  ) => SourceEvalResult | undefined;
}

export interface ColumnAggregate {
  count: number;
  sum: number;
  average: number;
}

// Numeric scores only: missing results, verdict-only evals, "n/a" strings and
// non-finite values are skipped, and don't count toward the denominator. A
// column with no numeric scores returns count=0 and renders blank.
export function computeColumnAggregates(
  evalResults: EvalResult[],
  sources: string[],
  getResultForSource: (
    evalResult: EvalResult,
    sourceId: string,
  ) => SourceEvalResult | undefined,
): ColumnAggregate[] {
  return evalResults.map((evalResult) => {
    let count = 0;
    let sum = 0;
    sources.forEach((sourceId) => {
      const result = getResultForSource(evalResult, sourceId);
      const score = result?.response.score;
      if (typeof score === 'number' && Number.isFinite(score)) {
        count += 1;
        sum += score;
      }
    });
    return {
      count,
      sum,
      average: count > 0 ? sum / count : 0,
    };
  });
}

// 4 sig figs is enough for typical 0–1 / 0–100 scoring schemes without
// flooding the cell, and trims trailing zeros for clean display.
export function formatAggregate(value: number): string {
  if (!Number.isFinite(value)) return '—';
  if (Number.isInteger(value)) return String(value);
  return Number(value.toPrecision(4)).toString();
}

export function TestResultTable({
  testResult,
  getAllSources,
  getResultForSource,
}: TestResultTableProps) {
  const tableRef = useRef<HTMLDivElement | null>(null);
  const evalResults: EvalResult[] = testResult.eval_results;
  const sources: string[] = getAllSources(evalResults);

  const aggregates = useMemo(
    () => computeColumnAggregates(evalResults, sources, getResultForSource),
    [evalResults, sources, getResultForSource],
  );
  const hasAnyNumericScores = aggregates.some((a) => a.count > 0);

  return (
    <div className='space-y-6'>
      <h2 className='text-2xl font-bold tracking-tight text-foreground'>
        {testResult.name}
      </h2>
      <div className='w-full overflow-x-auto'>
        <Table tableRef={tableRef}>
          <TableCaption>
            {`Test ran on ${safeFormatDate(new Date(testResult.created_at), 'MMMM d, yyyy')}`}
          </TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead className='w-[240px] min-w-[300px] max-w-[200px] break-words whitespace-normal'>
                Source
              </TableHead>
              {evalResults?.map((evalResult) => (
                <TableHead
                  key={evalResult.eval_id}
                  className='min-w-[150px] max-w-[100px] break-words whitespace-normal'
                >
                  {evalResult.eval_name || evalResult.eval_id}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sources?.map((sourceId) => (
              <TableRow key={sourceId}>
                <TableCell className='min-w-[150px] max-w-[100px] break-words whitespace-normal'>
                  {testResult.id_to_content?.[sourceId] ? (
                    <SourceContentRenderer
                      content={testResult.id_to_content[sourceId]}
                      sourceId={sourceId}
                    />
                  ) : (
                    sourceId
                  )}
                </TableCell>
                {evalResults?.map((evalResult) => {
                  const result = getResultForSource(evalResult, sourceId);
                  if (!result) {
                    return (
                      <TableCell
                        key={evalResult.eval_id}
                        className='min-w-[150px] max-w-[100px] break-words whitespace-normal'
                      >
                        N/A
                      </TableCell>
                    );
                  }
                  return (
                    <EvaluationResultCell
                      key={evalResult.eval_id}
                      result={result}
                      evalId={evalResult.eval_id}
                    />
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
          {hasAnyNumericScores && (
            <TableFooter>
              <TableRow>
                <TableCell className='min-w-[150px] max-w-[100px] font-medium'>
                  Sum
                </TableCell>
                {evalResults?.map((evalResult, idx) => {
                  const agg = aggregates[idx];
                  return (
                    <TableCell
                      key={evalResult.eval_id}
                      className='min-w-[150px] max-w-[100px] font-medium tabular-nums'
                    >
                      {agg.count > 0 ? formatAggregate(agg.sum) : ''}
                    </TableCell>
                  );
                })}
              </TableRow>
              <TableRow>
                <TableCell className='min-w-[150px] max-w-[100px] font-medium'>
                  Average
                </TableCell>
                {evalResults?.map((evalResult, idx) => {
                  const agg = aggregates[idx];
                  return (
                    <TableCell
                      key={evalResult.eval_id}
                      className='min-w-[150px] max-w-[100px] font-medium tabular-nums'
                      title={
                        agg.count > 0
                          ? `Avg of ${agg.count} numeric score${agg.count === 1 ? '' : 's'}`
                          : undefined
                      }
                    >
                      {agg.count > 0 ? formatAggregate(agg.average) : ''}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </div>
    </div>
  );
}
